<script setup>
import { Loc, ajax } from 'main.core';
import { onMounted, ref } from 'ui.vue3';
import { vMaska } from 'maska/vue';
import { useReCaptcha } from 'vue-recaptcha-v3';
import FormDialog from './FormDialog.vue';
import useCalculatorStore from '../../store/calculator.js';
import useUserStore from '../../store/user.js';

const calculator = useCalculatorStore();
const user = useUserStore();

const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

const fullName = ref('');
const phone = ref('');
const email = ref('');
const fullNameError = ref('');
const phoneError = ref('');
const emailError = ref('');
const sending = ref(false);
const sendOk = ref(false);
const errors = ref([]);

const fullNameInput = ref(null);
const phoneInput = ref(null);
const emailInput = ref(null);
const submitButton = ref(null);

onMounted(() => {
  if (user.fullName) {
    fullName.value = user.fullName;
  }
  if (user.phone) {
    phone.value = user.phone;
  }
  if (user.email) {
    email.value = user.email;
  }

  if (!fullName.value) {
    fullNameInput.value.focus({ focusVisible: true });
  } else if (!phone.value) {
    phoneInput.value.focus({ focusVisible: true });
  } else if (!email.value) {
    emailInput.value.focus({ focusVisible: true });
  } else {
    fullNameInput.value.focus({ focusVisible: true });
  }
});

const submit = async () => {
  await recaptchaLoaded();
  const googleRecaptchaResponse = await executeRecaptcha('cloud_request_fresh');

  sending.value = true;
  fullNameError.value = '';
  emailError.value = '';
  phoneError.value = '';
  errors.value = [];

  try {
    await ajax.runComponentAction('stsl:cloud.calculator', 'fresh', {
      method: 'POST',
      data: {
        program: calculator.selectedProgram,
        users: calculator.selectedUsers,
        bases: calculator.selectedBases,
        period: calculator.selectedPeriod,
        tariff: calculator.selectedTariff,
        full_name: fullName.value,
        phone: phone.value,
        email: email.value,
        'g-recaptcha-response': googleRecaptchaResponse,
      },
    });
    sendOk.value = true;
  } catch (e) {
    for (const error of e.errors) {
      if ('full_name' === error.code) {
        fullNameError.value = error.message;
      } else if ('phone' === error.code) {
        phoneError.value = error.message;
      } else if ('email' === error.code) {
        emailError.value = error.message;
      } else {
        errors.value.push(error.message);
      }
    }
  } finally {
    sending.value = false;
  }
};

</script>

<template>
  <FormDialog>
    <template #title>
      {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_FRESH_TITLE') }}
    </template>
    <div class="alert alert_success" v-if="sendOk">
      <p class="alert-heading">
        {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_FRESH_OK') }}
      </p>
    </div>
    <div class="alert alert_error" style="margin-bottom:16px" v-if="errors.length > 0">
      <p class="alert-heading">
        {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_FRESH_ERROR') }}
      </p>
      <p v-for="error in errors">
        {{ error }}
      </p>
    </div>
    <form method="POST" @submit.prevent="submit" v-if="!sendOk">
      <div class="form-input">
        <label for="cloud-calculator-fresh-full-name" class="form-input__label">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_FULL_NAME') }}:
        </label>
        <input
          ref="fullNameInput"
          type="text"
          id="cloud-calculator-fresh-full-name"
          autocomplete="name"
          class="form-input__input"
          :class="{ 'form-input__input_invalid': fullNameError }"
          :readonly="sending"
          v-model="fullName"
        >
        <div class="form-input__error" v-if="fullNameError">
          {{ fullNameError }}
        </div>
      </div>
      <div class="form-input">
        <label for="cloud-calculator-fresh-phone" class="form-input__label">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_PHONE') }}:
        </label>
        <input
          ref="phoneInput"
          type="tel"
          id="cloud-calculator-fresh-phone"
          autocomplete="tel"
          class="form-input__input"
          :class="{ 'form-input__input_invalid': phoneError }"
          :readonly="sending"
          v-model="phone"
          v-maska="'+7 (7##) ###-##-##'"
        >
        <div class="form-input__error" v-if="phoneError">
          {{ phoneError }}
        </div>
      </div>
      <div class="form-input">
        <label for="cloud-calculator-fresh-email" class="form-input__label">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_LABEL_EMAIL') }}:
        </label>
        <input
          ref="emailInput"
          type="email"
          id="cloud-calculator-fresh-email"
          class="form-input__input"
          autocomplete="email"
          :class="{ 'form-input__input_invalid': emailError }"
          :readonly="sending"
          v-model="email"
        >
        <div class="form-input__error" v-if="emailError">
          {{ emailError }}
        </div>
      </div>
      <div class="form-input form-input_buttons">
        <button ref="submitButton" type="submit" class="button" :disabled="sending">
          {{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_BUTTON_SUBMIT') }}
        </button>
      </div>
      <p><small>{{ Loc.getMessage('1CR_STSL_CLOUD_CALCULATOR_DIALOG_DISCLAIMER_PERSONAL_DATA') }}</small></p>
    </form>
  </FormDialog>
</template>
